import memoizeOne from 'memoize-one'
import { findIndex, findLastIndex, groupWith } from 'ramda'

import { EMPTY_ARRAY, getDateDiff, getDateTime } from '~/src/Lib/Utils'

const MINIMUM_EVENTS_PER_DAY = 3
const MAXIMUM_EVENTS_PER_WEEK = 9

const hasSameTimestampDay = (a, b) => getDateTime(a.timestamp).hasSame(getDateTime(b.timestamp), 'day')
const hasSameTimestampWeek = (a, b) => getDateTime(a.timestamp).hasSame(getDateTime(b.timestamp), 'week')

export const checkIfEventsAreWithinOneDay = (events = EMPTY_ARRAY) => (
  hasSameTimestampDay(events[0], events[events.length - 1])
)

export const groupEventsByDate = (events = EMPTY_ARRAY) => {
  if (events.length) {
    return groupWith(hasSameTimestampDay, events)
  }
  return EMPTY_ARRAY
}

export const groupEventsByWeek = (events = EMPTY_ARRAY) => {
  if (events.length) {
    return groupWith(hasSameTimestampWeek, events)
  }
  return EMPTY_ARRAY
}

export const groupEventsByAmountWithinDate = memoizeOne((events = []) => {
  const groupedByWeek = groupEventsByWeek(events)
  const regroupedWeeks = groupedByWeek.reduce((final, weekItems) => {
    if (!weekItems?.length) return final
    if (weekItems.length <= MAXIMUM_EVENTS_PER_WEEK) {
      final.push(weekItems)
    } else if (weekItems.length < (2 * MAXIMUM_EVENTS_PER_WEEK + 1)) {
      const { [MAXIMUM_EVENTS_PER_WEEK]: middle } = weekItems
      const finder = item => hasSameTimestampDay(middle, item)
      const firstMiddle = findIndex(finder, weekItems)
      const lastMiddle = findLastIndex(finder, weekItems)

      if (firstMiddle > (weekItems.length - lastMiddle) && weekItems.length - lastMiddle > 1) {
        final.push(weekItems.slice(0, firstMiddle + 1))
        final.push(weekItems.slice(firstMiddle + 1))
      } else {
        final.push(weekItems.slice(0, lastMiddle + 1))
        if (lastMiddle + 1 < weekItems.length) {
          final.push(weekItems.slice(lastMiddle + 1))
        }
      }
    } else {
      const groupedByDate = groupEventsByDate(weekItems)
      groupedByDate.forEach(eventsArr => {
        // simple check to avoid crash
        if (eventsArr && eventsArr.length) {
          // checking if this should be a new separate bucket or not
          if (eventsArr.length < MINIMUM_EVENTS_PER_DAY) {
            // check to avoid bugs when we're on 0 index
            if (final[final.length - 1]) {
              const { [final.length - 1]: previous } = final
              const tooManyInPrevious = eventsArr.length + previous.length > MAXIMUM_EVENTS_PER_WEEK
              const enoughEventsToBeASeparateBucket = previous.length >= MAXIMUM_EVENTS_PER_WEEK

              if (!tooManyInPrevious && !enoughEventsToBeASeparateBucket) {
                final[final.length - 1] = [...previous, ...eventsArr]
              }
            }
          }
          // create a new bucket
          final.push(eventsArr)
        }
      }, [])
    }
    return final
  }, [])
  return regroupedWeeks
})

export const getEventsDatePeriod = (events = []) => {
  if (events.length) {
    const startDate = events[0].timestamp
    const endDate = events[events.length - 1].timestamp

    const startDiff = getDateDiff(startDate)
    const endDiff = getDateDiff(endDate)

    const startDateFormatted = startDiff.daysDiff > -1 && startDiff.daysDiff < 1
      ? startDiff.text.charAt(0).toUpperCase() + startDiff.text.slice(1)
      : getDateTime(startDate).toLocaleString({ month: 'long', day: 'numeric' })
    const endDateFormatted = endDiff.daysDiff > -1 && endDiff.daysDiff < 1
      ? endDiff.text.charAt(0).toUpperCase() + endDiff.text.slice(1)
      : getDateTime(endDate).toLocaleString({ month: 'long', day: 'numeric' })

    return startDateFormatted === endDateFormatted
      ? startDateFormatted
      : `${endDateFormatted} - ${startDateFormatted}`
  }

  return 'No valid date'
}

export const getInitialDates = (days, filtered) => ({
  start: getDateTime('now')
    .minus({ days })
    .startOf(filtered ? 'week' : 'day'),
  end: getDateTime('now')
    .plus({ days })
    .endOf(filtered ? 'week' : 'day'),
})

export const getQueryParam = (query, paramName, defaultValue) => {
  const param = query[paramName]
  const paramType = typeof param
  if (paramType === 'undefined') return defaultValue
  const defaultType = typeof defaultValue
  if (paramType !== defaultType && paramType === 'string' && defaultValue != null) {
    try {
      return JSON.parse(param)
    } catch (e) {
      // eslint-disable-next-line no-console
      return defaultValue
    }
  }
  return param
}

export const getTaskCategory = (categories, { types = [], taskBehavior }) => {
  if (types.length === 1 && types.find(t => t === 'task')) {
    return Number(categories.find(category => !category.taskBehavior)?.id)
  }
  if (taskBehavior === 'PEST') {
    return Number(categories.find(category => category.taskBehavior === taskBehavior)?.id)
  }
  return null
}
